import React, { useRef, useState, useEffect } from 'react';
import { makeTr } from '../services/trLookup';
import { IntegrationPickupSlot } from '../integrations';
import { dhlFreigeschaltet } from '../services/dhlBridge';
import { isWiesnAccount, isBevollmaechtigterEnabled, compressVollmachtPhoto } from '../services/dhlUsbStick';

/**
 * ✍️ Unterschrift-Dialog bei Paket-Ausgabe (Büro, Empfang, Wohnanlage, Gepäck & DHL-Paketshop)
 *
 * Unterstützt:
 *  - Finger, Touchpen und Maus auf HTML5-Canvas + Vektor-Strokes (0..1) für den DHL USB-Stick
 *  - Mehrere Sendungen desselben Empfängers mit nur 1 Unterschrift ausgeben (`weiterePakete`)
 *  - Ausgabe an Bevollmächtigten (Vorname, Nachname & Foto der Vollmacht) für freigeschaltete Konten
 */
export default function SignatureModal({
  paket,
  weiterePakete = [],
  initialMode = 'empfaenger',
  colors,
  lang = 'de',
  onConfirm,
  onCancel,
  onClose,
  onSkip,
}) {
  // Der Aufrufer (AddPackageWithAI) übergibt onClose/onSkip — ohne diese Zuordnung ließ sich der Dialog nicht
  // abbrechen, der einzige Ausweg war die Ausgabe (Review 19.10.2026)
  const abbrechen = onCancel || onClose || onSkip || null;
  const dark = !!colors?.darkMode;
  const c = {
    bg: dark ? '#23272f' : '#ffffff',
    card: dark ? '#1b1f27' : '#f6f8fa',
    text: dark ? '#ffffff' : '#1f2328',
    sub: dark ? '#9ca3af' : '#57606a',
    border: dark ? '#374151' : '#d0d7de',
    primary: '#2e7d32',
    purple: '#7c3aed',
  };

  const tr = makeTr(lang);
  const dhlAllowed = isWiesnAccount() && dhlFreigeschaltet();
  const showBevollmOption = dhlAllowed && (isBevollmaechtigterEnabled() || initialMode === 'bevollmaechtigter');

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const strokesRef = useRef([]);
  const currentStrokeRef = useRef(null);
  const vollmachtInputRef = useRef(null);

  const [hasSigned, setHasSigned] = useState(false);
  const [abholerName, setAbholerName] = useState('');
  const [abholArt, setAbholArt] = useState(initialMode === 'bevollmaechtigter' ? 'bevollmaechtigter' : 'empfaenger');
  const [bevollmVorname, setBevollmVorname] = useState('');
  const [bevollmNachname, setBevollmNachname] = useState('');
  const [vollmachtFoto, setVollmachtFoto] = useState('');
  const [vollmachtBusy, setVollmachtBusy] = useState(false);

  // Multi-Paket-Auswahl: Hauptpaket + alle weiteren offenen Pakete desselben Empfängers standardmäßig angehakt
  const [selectedExtraIds, setSelectedExtraIds] = useState(() =>
    (Array.isArray(weiterePakete) ? weiterePakete : []).map((p) => p?.id).filter(Boolean)
  );

  useEffect(() => {
    setSelectedExtraIds((Array.isArray(weiterePakete) ? weiterePakete : []).map((p) => p?.id).filter(Boolean));
  }, [weiterePakete]);

  useEffect(() => {
    if (initialMode === 'bevollmaechtigter') {
      setAbholArt('bevollmaechtigter');
    }
  }, [initialMode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = dark ? '#ffffff' : '#111827';
    ctx.lineWidth = 2.6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [dark]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0, nx: 0, ny: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const nx = Math.max(0, Math.min(1, (clientX - rect.left) / (rect.width || 1)));
    const ny = Math.max(0, Math.min(1, (clientY - rect.top) / (rect.height || 1)));
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
      nx: +nx.toFixed(3),
      ny: +ny.toFixed(3),
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y, nx, ny } = getPos(e);
    drawingRef.current = true;
    currentStrokeRef.current = [[nx, ny]];
    strokesRef.current.push(currentStrokeRef.current);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y, nx, ny } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (currentStrokeRef.current) {
      currentStrokeRef.current._last = [nx, ny];
      const prev = currentStrokeRef.current[currentStrokeRef.current.length - 1];
      if (!prev || Math.hypot(nx - prev[0], ny - prev[1]) > 0.014) {
        currentStrokeRef.current.push([nx, ny]);
      }
    }
    if (!hasSigned) setHasSigned(true);
  };

  const endDraw = () => {
    if (drawingRef.current && currentStrokeRef.current && currentStrokeRef.current._last) {
      const last = currentStrokeRef.current._last;
      const prev = currentStrokeRef.current[currentStrokeRef.current.length - 1];
      if (!prev || prev[0] !== last[0] || prev[1] !== last[1]) {
        currentStrokeRef.current.push(last);
      }
      delete currentStrokeRef.current._last;
    }
    drawingRef.current = false;
    currentStrokeRef.current = null;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    strokesRef.current = [];
    currentStrokeRef.current = null;
    setHasSigned(false);
  };

  const handleVollmachtFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVollmachtBusy(true);
    try {
      const compressed = await compressVollmachtPhoto(file);
      setVollmachtFoto(compressed);
    } catch {
      // Fallback
    } finally {
      setVollmachtBusy(false);
      e.target.value = '';
    }
  };

  const toggleExtraPaket = (id) => {
    setSelectedExtraIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const isBevollm = abholArt === 'bevollmaechtigter';
  const bevollmFullName = [bevollmVorname.trim(), bevollmNachname.trim()].filter(Boolean).join(' ');
  // Vorname ist bei DHL kein Pflichtfeld -> nur Nachname + Vollmacht-Foto sind Pflicht!
  const canConfirmBevollm = !isBevollm || (
    bevollmNachname.trim().length > 0 &&
    Boolean(vollmachtFoto)
  );
  const totalSelectedCount = 1 + selectedExtraIds.length;

  const handleConfirm = (skipSig = false) => {
    let dataUrl = null;
    if (!skipSig && hasSigned && canvasRef.current) {
      try { dataUrl = canvasRef.current.toDataURL('image/png'); } catch { /* */ }
    }
    const effectiveAbholer = isBevollm
      ? (bevollmFullName ? `${bevollmFullName} (Bevollm.)` : abholerName.trim())
      : abholerName.trim();
    const allPaketIds = [paket?.id, ...selectedExtraIds].filter(Boolean);

    onConfirm?.({
      signed: !skipSig && hasSigned,
      abholerName: effectiveAbholer,
      signatureDataUrl: dataUrl,
      strokes: !skipSig && hasSigned ? strokesRef.current : [],
      abholArt: isBevollm ? 'bevollmaechtigter' : 'empfaenger',
      bevollmaechtigter: isBevollm
        ? { vorname: bevollmVorname.trim(), nachname: bevollmNachname.trim() }
        : null,
      vollmachtFoto: isBevollm ? vollmachtFoto : '',
      selectedPaketIds: allPaketIds,
      timestamp: new Date().toISOString(),
    });
  };

  return (
    <div
      data-testid="signature-modal"
      onClick={(e) => { if (e.target === e.currentTarget) abbrechen?.(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3600,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 14,
      }}
    >
      <div style={{
        background: c.bg, color: c.text, borderRadius: 16, width: '100%', maxWidth: 480,
        maxHeight: '92vh', overflowY: 'auto',
        padding: 20, boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <strong style={{ fontSize: 18 }}>
            {isBevollm ? '👥 ' : '✍️ '}
            {isBevollm
              ? tr('Ausgabe an Bevollmächtigten', 'Handout to Authorized Person', 'Uitgifte aan gemachtigde')
              : tr('Unterschrift bei Ausgabe', 'Signature on Pickup', 'Handtekening bij afhalen')}
          </strong>
          <button
            type="button"
            onClick={abbrechen}
            style={{ background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: c.text, lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        {/* 👤 Empfänger vs. 👥 Bevollmächtigter Umschalter (nur wenn für Konto aktiviert) */}
        {showBevollmOption && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button
              type="button"
              data-testid="signature-mode-empfaenger"
              onClick={() => setAbholArt('empfaenger')}
              style={{
                flex: 1, padding: '9px 10px', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                border: `2px solid ${!isBevollm ? c.primary : c.border}`,
                background: !isBevollm ? (dark ? 'rgba(46,125,50,0.22)' : '#e8f5e9') : 'transparent',
                color: c.text,
              }}
            >
              👤 {tr('Empfänger selbst', 'Recipient', 'Ontvanger zelf')}
            </button>
            <button
              type="button"
              data-testid="signature-mode-bevollmaechtigter"
              onClick={() => setAbholArt('bevollmaechtigter')}
              style={{
                flex: 1, padding: '9px 10px', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                border: `2px solid ${isBevollm ? c.purple : c.border}`,
                background: isBevollm ? (dark ? 'rgba(124,58,237,0.25)' : '#f3e8ff') : 'transparent',
                color: c.text,
              }}
            >
              👥 {tr('Bevollmächtigter', 'Authorized Rep.', 'Gemachtigde')}
            </button>
          </div>
        )}

        {/* Paket-Info */}
        <div style={{
          padding: '10px 12px', borderRadius: 10, background: c.card, border: `1px solid ${c.border}`,
          marginBottom: 12, fontSize: 14,
        }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{paket?.name || '–'}</div>
          <div style={{ color: c.sub, fontSize: 13, marginTop: 2 }}>
            {[
              paket?.streetAndNumber,
              paket?.regalplatz && `${tr('Fach', 'Shelf', 'Vak')}: ${paket.regalplatz}`,
              (paket?.trackingNumber || paket?.sendungsnummer) && `#${String(paket?.trackingNumber || paket?.sendungsnummer).slice(-8)}`,
            ].filter(Boolean).join(' · ')}
          </div>

          {/* 📦 Multi-Paket-Auswahl (mehrere Sendungen mit nur 1 Unterschrift ausgeben!) */}
          {Array.isArray(weiterePakete) && weiterePakete.length > 0 && (
            <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px dashed ${c.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b', marginBottom: 6 }}>
                📦 {tr(
                  `${weiterePakete.length} weitere offene Sendung(en) für diesen Kunden mit 1 Unterschrift ausgeben:`,
                  `Hand out ${weiterePakete.length} additional parcel(s) with 1 signature:`,
                  `${weiterePakete.length} extra pakket(ten) met 1 handtekening uitgeven:`
                )}
              </div>
              {weiterePakete.map((wp) => {
                const checked = selectedExtraIds.includes(wp.id);
                const code = wp.trackingNumber || wp.sendungsnummer || '';
                return (
                  <label
                    key={wp.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px',
                      borderRadius: 6, cursor: 'pointer', fontSize: 13,
                      background: checked ? (dark ? 'rgba(16,185,129,0.12)' : '#ecfdf5') : 'transparent',
                      marginBottom: 4,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleExtraPaket(wp.id)}
                      style={{ width: 17, height: 17 }}
                    />
                    <span style={{ flex: 1 }}>
                      <b>{wp.regalplatz ? `Fach ${wp.regalplatz}` : wp.name}</b>
                      {code ? ` · …${String(code).slice(-8)}` : ''}
                      {wp.paketdienst ? ` (${String(wp.paketdienst).toUpperCase()})` : ''}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* 🧩 Modul-Slot für Sonderkunden (z.B. Grenzpaket Tarif-Banner) */}
        <IntegrationPickupSlot paket={paket} colors={colors} lang={lang} />

        {/* 👥 Felder für Bevollmächtigten (Vorname + Nachname + Foto der Vollmacht) */}
        {isBevollm ? (
          <div style={{
            padding: 12, borderRadius: 10, marginBottom: 12,
            border: `1.5px solid ${c.purple}`, background: dark ? 'rgba(124,58,237,0.12)' : '#faf5ff',
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: dark ? '#c4b5fd' : '#6d28d9' }}>
              👥 {tr('Angaben zum Bevollmächtigten & Vollmacht-Foto', 'Authorized Person & Power of Attorney Photo', 'Gegevens gemachtigde & foto volmacht')}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                type="text"
                data-testid="signature-bevollm-nachname"
                value={bevollmNachname}
                onChange={(e) => setBevollmNachname(e.target.value)}
                placeholder={tr('Nachname des Bevollm. *', 'Last name *', 'Achternaam *')}
                style={{
                  flex: 1, minWidth: 0, boxSizing: 'border-box', padding: '9px 10px', borderRadius: 8,
                  border: `1.5px solid ${bevollmNachname.trim() ? c.purple : '#ef4444'}`,
                  background: c.bg, color: c.text, fontSize: 14, fontWeight: 600,
                }}
              />
              <input
                type="text"
                data-testid="signature-bevollm-vorname"
                value={bevollmVorname}
                onChange={(e) => setBevollmVorname(e.target.value)}
                placeholder={tr('Vorname (optional)', 'First name (optional)', 'Voornaam (optioneel)')}
                style={{
                  flex: 1, minWidth: 0, boxSizing: 'border-box', padding: '9px 10px', borderRadius: 8,
                  border: `1px solid ${bevollmVorname.trim() ? c.purple : c.border}`,
                  background: c.bg, color: c.text, fontSize: 14,
                }}
              />
            </div>

            <input
              ref={vollmachtInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleVollmachtFile}
              style={{ display: 'none' }}
            />

            {!vollmachtFoto ? (
              <button
                type="button"
                data-testid="signature-vollmacht-capture"
                onClick={() => vollmachtInputRef.current?.click()}
                disabled={vollmachtBusy}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  border: `1.5px dashed ${c.purple}`, background: c.bg, color: c.text,
                  fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                📷 {vollmachtBusy
                  ? tr('Komprimiere Foto…', 'Compressing photo…', 'Foto comprimeren…')
                  : tr('Foto der Vollmacht aufnehmen / auswählen', 'Take photo of power of attorney', 'Foto van volmacht maken')}
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: c.bg, padding: 8, borderRadius: 8, border: `1px solid ${c.border}` }}>
                <img
                  src={vollmachtFoto}
                  alt="Vollmacht"
                  style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: `1px solid ${c.purple}` }}
                />
                <div style={{ flex: 1, fontSize: 12 }}>
                  <div style={{ fontWeight: 700, color: '#10b981' }}>✅ {tr('Vollmacht-Foto gespeichert', 'Authorization photo ready', 'Volmacht-foto gereed')}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <button
                      type="button"
                      onClick={() => vollmachtInputRef.current?.click()}
                      style={{ padding: '4px 8px', borderRadius: 6, border: `1px solid ${c.border}`, background: 'transparent', color: c.text, fontSize: 11, cursor: 'pointer' }}
                    >
                      🔄 {tr('Neu aufnehmen', 'Retake', 'Opnieuw')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setVollmachtFoto('')}
                      style={{ padding: '4px 8px', borderRadius: 6, border: `1px solid ${c.border}`, background: 'transparent', color: '#ef4444', fontSize: 11, cursor: 'pointer' }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Optional: Abholer-Name falls abweichend */
          <input
            type="text"
            data-testid="signature-abholer"
            value={abholerName}
            onChange={(e) => setAbholerName(e.target.value)}
            placeholder={tr('Abgeholt von (optional, falls abweichend)', 'Picked up by (optional)', 'Afgehaald door (optioneel)')}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8,
              border: `1px solid ${c.border}`, background: c.card, color: c.text, fontSize: 13, marginBottom: 10,
            }}
          />
        )}

        {/* Unterschriften-Feld */}
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <canvas
            ref={canvasRef}
            data-testid="signature-canvas"
            width={420}
            height={170}
            onMouseDown={startDraw}
            onMouseMove={moveDraw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={moveDraw}
            onTouchEnd={endDraw}
            style={{
              width: '100%', height: 160, borderRadius: 10,
              border: `2px dashed ${hasSigned ? (isBevollm ? c.purple : c.primary) : c.border}`,
              background: dark ? '#14171d' : '#fcfcfd',
              touchAction: 'none', cursor: 'crosshair', display: 'block',
            }}
          />
          {!hasSigned && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none', color: c.sub, fontSize: 14, textAlign: 'center', padding: '0 12px',
            }}>
              ✍️ {isBevollm
                ? tr('Unterschrift des Bevollmächtigten hier leisten', 'Authorized representative signs here', 'Handtekening gemachtigde hier')
                : tr('Hier mit Finger oder Stift unterschreiben', 'Sign here with finger or stylus', 'Teken hier met vinger of pen')}
            </div>
          )}
          {hasSigned && (
            <button
              type="button"
              onClick={clearCanvas}
              style={{
                position: 'absolute', top: 8, right: 8, padding: '4px 10px', borderRadius: 6,
                border: `1px solid ${c.border}`, background: c.bg, color: c.text, fontSize: 12, cursor: 'pointer',
              }}
            >
              🧹 {tr('Neu unterschreiben', 'Clear & Re-sign', 'Opnieuw tekenen')}
            </button>
          )}
        </div>
        <div style={{ fontSize: 11, color: c.sub, marginTop: -6, marginBottom: 10 }}>
          ℹ️ {tr(
            'Kunde kann beliebig oft neu unterschreiben — Übertragung erst nach Klick auf „Bestätigen & Ausgeben".',
            'Customer can re-sign anytime — transmitted only after clicking Confirm.',
            'Klant kan opnieuw tekenen — verzending pas na bevestigen.'
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          {!isBevollm && (
            <button
              type="button"
              data-testid="signature-skip"
              onClick={() => handleConfirm(true)}
              style={{
                padding: '11px 12px', borderRadius: 10, border: `1px solid ${c.border}`,
                background: 'transparent', color: c.sub, fontSize: 13, fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {tr('Ohne Unterschrift', 'Skip signature', 'Zonder handtekening')}
            </button>
          )}
          <button
            type="button"
            data-testid="signature-confirm"
            disabled={!hasSigned || !canConfirmBevollm}
            onClick={() => handleConfirm(false)}
            style={{
              flex: 1, padding: '11px 14px', borderRadius: 10, border: 'none',
              background: (hasSigned && canConfirmBevollm) ? (isBevollm ? c.purple : c.primary) : c.border,
              color: '#fff',
              fontSize: 14, fontWeight: 700,
              cursor: (hasSigned && canConfirmBevollm) ? 'pointer' : 'not-allowed',
            }}
          >
            ✅ {totalSelectedCount > 1
              ? tr(`${totalSelectedCount} Sendungen bestätigen & ausgeben`, `Confirm & Hand Out (${totalSelectedCount})`, `${totalSelectedCount} pakketten uitgeven`)
              : tr('Bestätigen & Ausgeben', 'Confirm & Hand Out', 'Bevestigen & Uitgeven')}
          </button>
        </div>
      </div>
    </div>
  );
}
